import React from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Globe2,
  FileCheck2,
  Plane,
  TrainFront,
  Hotel,
  CarFront,
  HeartHandshake,
  ArrowRight,
} from "lucide-react";
import TiltCard from "./TiltCard";
import { SERVICES } from "../data/site";

const ICONS = {
  MapPin,
  Globe2,
  FileCheck2,
  Plane,
  TrainFront,
  Hotel,
  CarFront,
  HeartHandshake,
};

export default function Services() {
  const handleEnquire = (title) => {
    const el = document.getElementById("contact");
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: "smooth" });
    }
    setTimeout(() => {
      const sel = document.querySelector('[data-testid="contact-form-message"]');
      if (sel) {
        sel.value = `Hi, I'd like to know more about: ${title}`;
        sel.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }, 600);
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 sm:py-32 aurora-bg grain"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.24em] text-brand-orange font-semibold">
            What We Do
          </div>
          <h2
            data-testid="services-heading"
            className="mt-3 font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05] tracking-tight text-balance"
          >
            Everything you need for the{" "}
            <span className="italic text-brand-orange">perfect trip</span> —
            under one roof.
          </h2>
          <p className="mt-5 text-slate-600 text-lg leading-relaxed max-w-2xl">
            From flight tickets to full-stack honeymoon packages, our team
            handles the planning so you can focus on the memories.
          </p>
          {/* Inline service checklist for scanability */}
          <div className="mt-5 flex flex-wrap gap-2">
            {["Flights", "Hotels", "Visa", "Trains", "Cars", "Group Tours"].map((s) => (
              <span
                key={s}
                className="px-3 py-1 rounded-full text-sm bg-white/80 border border-slate-200 text-slate-700 font-medium shadow-sm"
              >
                ✓ {s}
              </span>
            ))}
          </div>
        </div>

        {/* Cards grid — consistent layout across all cards */}
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || MapPin;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.7,
                  delay: i * 0.06,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <TiltCard
                  intensity={10}
                  className="h-full"
                  data-testid={`service-card-${i}`}
                >
                  <div
                    className="relative h-full rounded-3xl p-7 glass overflow-hidden group transition-all duration-500 hover:shadow-[0_24px_60px_rgba(15,23,42,0.18)] hover:-translate-y-1 flex flex-col"
                    style={{ transformStyle: "preserve-3d" }}
                  >
                    {/* Soft accent gradient */}
                    <div
                      className={`absolute -top-16 -right-16 w-44 h-44 rounded-full bg-gradient-to-br ${s.accent} blur-2xl opacity-70 group-hover:opacity-100 transition-opacity`}
                    />
                    <div
                      className="relative flex flex-col gap-5 flex-1"
                      style={{ transform: "translateZ(36px)" }}
                    >
                      {/* Icon — consistent sizing */}
                      <div className="h-14 w-14 rounded-2xl bg-white shadow-md ring-1 ring-orange-100 flex items-center justify-center group-hover:scale-110 transition-transform duration-500 shrink-0">
                        <Icon className="w-7 h-7 text-brand-ocean" strokeWidth={1.7} />
                      </div>

                      {/* Text — consistent height with flex grow */}
                      <div className="flex-1">
                        <h3 className="font-playfair text-[1.2rem] font-bold text-slate-900 leading-snug">
                          {s.title}
                        </h3>
                        <p className="mt-2 text-[0.9rem] text-slate-600 leading-relaxed line-clamp-3">
                          {s.desc}
                        </p>
                      </div>

                      {/* CTA — always at bottom, consistent style */}
                      <button
                        data-testid={`service-cta-${i}`}
                        onClick={() => handleEnquire(s.title)}
                        className="mt-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-ocean/8 border border-brand-ocean/20 text-sm font-semibold text-brand-ocean hover:bg-brand-ocean hover:text-white transition-all duration-300 w-fit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ocean"
                      >
                        Enquire now
                        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                      </button>
                    </div>
                  </div>
                </TiltCard>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
